import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp, Search } from 'lucide-react';

const FAQPage = () => {
  const [expandedItems, setExpandedItems] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    document.title = 'FAQ - SynaptopathyDB';
  }, []);

  const categories = [
    { id: 'all', name: 'All Questions' },
    { id: 'general', name: 'General Information' },
    { id: 'data', name: 'Database & Data' },
    { id: 'search', name: 'Search Functionality' },
    { id: 'gene', name: 'Gene Search' },
    { id: 'disease', name: 'Disease Search' },
    { id: 'paper', name: 'Publication Search' },
    { id: 'technical', name: 'Technical Help' }
  ];

  const faqItems = [
    {
      id: 1,
      question: 'What is SynaptopathyDB?',
      answer: 'SynaptopathyDB is an integrated database that connects data from 64 mammalian synapse proteome studies with multiple genetic and phenotypic resources. It enables targeted searches across disease categories, phenotypes, genes and synaptic compartments, highlighting the extensive involvement of synaptic gene mutations in neurological, psychiatric, developmental, and other systemic disorders.',
      category: 'general'
    },
    {
      id: 2,
      question: 'What data is included in SynaptopathyDB?',
      answer: 'SynaptopathyDB includes data on 3,437 synapse proteins reported in five or more synapse proteomic studies, with 1,236 of these genes linked to 347 central and peripheral nervous system disorders. The database includes information on gene mutations, associated publications, disease links, and protein localization within different synaptic compartments (presynaptic, synaptic vesicles, postsynaptic, and synaptosome).',
      category: 'data'
    },
    {
      id: 3,
      question: 'How can I search for a specific gene?',
      answer: 'You can search for genes using the Gene Search feature. Enter a gene name (e.g., SHANK3) or ID (Entrez ID, MGI ID) in the search box. You can also search using protein names. The system will retrieve all relevant information including associated diseases, mutations, and publications where the gene was identified. The results page allows you to filter and explore detailed information about the gene.',
      category: 'gene'
    },
    {
      id: 4,
      question: 'Can I search for proteins rather than genes?',
      answer: 'SynaptopathyDB supports protein name searches, which will identify and retrieve all genes related to that protein. When you enter a protein name in the search box, the system will look up associated gene symbols and display comprehensive information about those genes. This feature helps bridge the gap between protein-level and gene-level data.',
      category: 'search'
    },
    {
      id: 5,
      question: 'How do I find diseases related to synaptic genes?',
      answer: 'Use the Disease Search feature to search by disease name or ID (HDOID). This will retrieve all information about the disease, including associated genes, mutations, and relevant publications. You can also start from the Gene Search if you know a specific gene and want to see which diseases are associated with it.',
      category: 'disease'
    },
    {
      id: 6,
      question: 'What are "consensus genes" in SynaptopathyDB?',
      answer: 'Consensus genes are synaptic proteins that have been identified in multiple studies (five or more), reducing the likelihood that they are contaminants. These 3,437 proteins form the core dataset of SynaptopathyDB. In search results, consensus genes are marked with a special label to indicate their high confidence status.',
      category: 'data'
    },
    {
      id: 7,
      question: 'How do I find original publications where synaptic proteins were identified?',
      answer: 'Use the Synapse Proteomic Publication Search to find original research publications. You can search by publication title, PMID, year, or description. The search results will show you detailed information about each publication, including associated genes, their localizations, and experimental methods used to identify them.',
      category: 'paper'
    },
    {
      id: 8,
      question: 'What information is available about synaptic localizations?',
      answer: 'SynaptopathyDB contains information about protein localization within different synaptic compartments. This information is available in gene search results and can be filtered using the localization filter.',
      category: 'data'
    },
    {
      id: 9,
      question: 'How accurate is the disease-gene association data?',
      answer: 'The disease-gene associations in SynaptopathyDB are derived from established databases including OMIM and ICD-11, representing known and clinically validated connections. Our database provides information on 1,236 genes with mutations that have been linked to 347 central and peripheral nervous system disorders.',
      category: 'data'
    },
    {
      id: 10,
      question: 'Can I download the data from SynaptopathyDB?',
      answer: 'SynaptopathyDB provides several API endpoints that allow you to download data in CSV format. These include endpoints for basic tables (genes, diseases, publications, mutations) as well as relationship-based data (genes-diseases, mutations-publications-genes, etc.). For more information, please visit our Documentation page.',
      category: 'technical'
    },
    {
      id: 11,
      question: 'Why does the search identify my protein as a gene?',
      answer: 'When you search for a protein name, our system automatically uses MyGene.info to convert the protein name to gene symbols. It then searches for these gene symbols in our database. This approach allows you to access gene-level information even when starting with a protein name. The protein lookup status will inform you about the genes found related to your protein query.',
      category: 'search'
    },
    {
      id: 12,
      question: 'How do I interpret mutation information?',
      answer: 'Mutation information includes details such as chromosome location, position, variant type, function class, cDNA and protein variants, and whether the mutation is de novo or associated with specific conditions (tagged as SFARI — autism‑risk gene, Epilepsy — gene/variant linked to seizure disorders, or ClinVar — clinically classified variant). For each mutation, you can also see associated publications by their PubMed IDs, which you can click to access the original research.',
      category: 'data'
    },
    {
      id: 13,
      question: 'What is the difference between the different search options?',
      answer: 'SynaptopathyDB offers three main search options: Gene Search (for finding genes and their associated diseases, mutations, and publications), Disease Search (for finding diseases and their associated genes, mutations, and publications), and Synapse Proteomic Publication Search (for finding research publications and the genes they identified). The unified search on the homepage allows you to search across all these categories simultaneously.',
      category: 'search'
    },
    {
      id: 14,
      question: 'How is SynaptopathyDB different from other gene databases?',
      answer: 'SynaptopathyDB is uniquely focused on the synapse proteome and its connection to diseases. Unlike general gene databases, it provides specific information about protein localization within synaptic compartments and integrates data from 64 specialized synapse proteome studies. It also emphasizes the relationship between synaptic genes and a wide range of disorders beyond just neurological diseases.',
      category: 'general'
    },
    {
      id: 15,
      question: 'Who developed SynaptopathyDB?',
      answer: 'SynaptopathyDB was developed by a team of experts in synaptic proteomics, including Oksana Sorokina, Digin Dominic, Alex Bayés, J. Douglas Armstrong, and Seth G.N. Grant. The database represents a collaborative effort to integrate diverse datasets and provide a comprehensive resource for synaptopathy research.',
      category: 'general'
    },
    {
      id: 16,
      question: 'What does the Highlight feature do in search results?',
      answer: 'The Highlight feature helps you quickly identify your search terms within the search results. When enabled, your search terms will be highlighted in blue throughout the results, making it easier to see why a particular item was included in your results. This feature is available in Gene Search, Disease Search, and Synapse Proteomic Publication Search.',
      category: 'search'
    },
    {
      id: 17,
      question: 'My search returned no results. What should I do?',
      answer: 'If your search returns no results, try these troubleshooting steps: 1) Check your spelling and try alternative terms. 2) If searching for a protein, try using a gene symbol instead. 3) Use broader terms to widen your search. 4) Check if your term is in our database by trying the autocomplete feature, which suggests matching terms as you type. 5) If searching for a disease, try using the Disease Ontology ID (Human Disease Ontology Identifier) if you know it.',
      category: 'technical'
    },
    {
      id: 18,
      question: 'How do I filter results in Gene Search?',
      answer: 'In Gene Search, after obtaining your initial results, you can expand a gene card and use the filters provided in each section. For publications, you can filter by text in any field or by localization using the dropdown menu. For diseases and mutations, you can explore the results that are already categorized. The highlight feature helps you identify matching terms in the filtered results.',
      category: 'gene'
    },
    {
      id: 19,
      question: 'How do I filter results in Disease Search?',
      answer: 'In Disease Search, after expanding a disease card, you can filter genes by name or ID using the text filter. You can also filter to show only consensus genes, non-consensus genes, or all genes using the dropdown menu. For each gene, you can view associated publications, and for mutations, you can see detailed information about each variant.',
      category: 'disease'
    },
    {
      id: 20,
      question: 'How do I filter results in Synapse Proteomic Publication Search?',
      answer: 'In Synapse Proteomic Publication Search, after expanding a publication card, you can filter associated genes by text in any field or by localization using the dropdown menu. You can also filter to show only consensus genes, non-consensus genes, or all genes. For each gene, you can see detailed information including Entrez IDs, MGI ID, and localization within the synapse.',
      category: 'paper'
    }
  ];

  const toggleItem = (id) => {
    setExpandedItems((prev) => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const filterFAQs = () => {
    return faqItems.filter((item) => {
      // Filter by category
      const categoryMatch = activeCategory === 'all' || item.category === activeCategory;

      // Filter by search term
      const searchMatch =
        searchTerm === '' ||
        item.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.answer.toLowerCase().includes(searchTerm.toLowerCase());

      return categoryMatch && searchMatch;
    });
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h1>

        {/* Search bar */}
        <div className="mb-8 relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            className="block w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md leading-5 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Search FAQs..."
            value={searchTerm}
            onChange={handleSearchChange}
          />
        </div>

        {/* Category tabs */}
        <div className="mb-8 border-b border-gray-200">
          <nav className="-mb-px flex space-x-4">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`whitespace-nowrap py-2 px-1 border-b-2 text-sm font-medium ${activeCategory === category.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                {category.name}
              </button>
            ))}
          </nav>
        </div>

        {/* FAQ accordion */}
        <div className="space-y-4">
          {filterFAQs().map((item) => (
            <div
              key={item.id}
              className="bg-white overflow-hidden shadow rounded-lg"
            >
              <button
                onClick={() => toggleItem(item.id)}
                className="w-full px-4 py-5 flex justify-between items-center focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
              >
                <span className="text-lg font-medium text-gray-900 text-left">
                  {searchTerm && item.question.toLowerCase().includes(searchTerm.toLowerCase()) ? (
                    highlightText(item.question, searchTerm)
                  ) : (
                    item.question
                  )}
                </span>
                <div className="ml-4 flex-shrink-0">
                  {expandedItems[item.id] ? (
                    <ChevronUp className="h-5 w-5 text-gray-500" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-500" />
                  )}
                </div>
              </button>

              {expandedItems[item.id] && (
                <div className="px-4 py-5 border-t border-gray-200">
                  <p className="text-base text-gray-700">
                    {searchTerm && item.answer.toLowerCase().includes(searchTerm.toLowerCase()) ? (
                      highlightText(item.answer, searchTerm)
                    ) : (
                      item.answer
                    )}
                  </p>
                </div>
              )}
            </div>
          ))}

          {filterFAQs().length === 0 && (
            <div className="bg-white overflow-hidden shadow rounded-lg p-6 text-center">
              <p className="text-gray-500">No FAQs match your search criteria. Please try different keywords.</p>
            </div>
          )}
        </div>

        {/* Contact section */}
        <div className="mt-12 bg-blue-50 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Still have questions?</h2>
          <p className="text-gray-700 mb-4">
            If you couldn't find the answer to your question, feel free to contact our team.
          </p>
          <a
            href="/about#contact"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
          >
            Contact Us
          </a>
        </div>
      </div>
    </div>
  );
};

// Helper function to highlight search terms
const highlightText = (text, term) => {
  if (!term || !text) return text;

  const escapeRegExp = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  const escapedTerm = escapeRegExp(term);
  const regex = new RegExp(`(${escapedTerm})`, 'gi');
  const parts = text.toString().split(regex);

  return (
    <span>
      {parts.map((part, i) =>
        regex.test(part) ? (
          <span key={i} className="bg-blue-100 text-blue-900 px-0.5 rounded font-medium">
            {part}
          </span>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </span>
  );
};

export default FAQPage;